import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import { useTheme } from "@mui/material/styles";

export default function TopPromoBanner({
  message = "Limited time offer on annual plans",
  highlight = "Save 50%",
  ctaText = "View Plans",
  href = "/pricing",
  storageKey = "promo_top_soft_v1",
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const barRef = useRef(null);
  const hasAnimated = useRef(false);

  // Correct initial visibility
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(storageKey) !== "1";
  });

  const [height, setHeight] = useState("auto");
  const [enableAnimation, setEnableAnimation] = useState(false);

  // Measure height BEFORE paint
  useLayoutEffect(() => {
    if (barRef.current && open) {
      const measured = barRef.current.scrollHeight;
      setHeight(measured);
    }
  }, [open, isMobile]);

  // Enable animation AFTER first layout pass
  useEffect(() => {
    if (!hasAnimated.current) {
      hasAnimated.current = true;
      setEnableAnimation(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
  };

  return (
    <Box
      sx={{
        height: open ? height : 0,
        overflow: "hidden",
        transition: enableAnimation ? "height 220ms ease" : "none",
        background: "#f7f5f4",
        borderBottom: open ? "1px solid rgba(0,0,0,0.08)" : "none",
      }}
    >
      <Box
        ref={barRef}
        sx={{
          opacity: open ? 1 : 0,
          transition: enableAnimation ? "opacity 150ms ease" : "none",
          py: 1,
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              fontWeight: 500,
              color: "#1a1f2b",
            }}
          >
            <LocalOfferRoundedIcon
              sx={{
                fontSize: isMobile ? 18 : 20,
                color: "#12b2f1",
              }}
            />
            <Box
              component="span"
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: "6px",
                backgroundColor: "rgba(18, 178, 241, 0.15)",
                fontWeight: 700,
                fontSize: isMobile ? "0.7rem" : "0.8rem",
                color: "#0b1220",
              }}
            >
              {highlight}
            </Box>
            {message}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              href={href}
              variant="contained"
              size="small"
              sx={{
                boxShadow: "none",
                color: "#0b1220",
                bgcolor: "white",
                border: "1px solid rgba(0,0,0,0.05)",
                "&:hover": { bgcolor: "#f8fafc" },
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
                px: 1.5,
                py: 0.3,
              }}
            >
              {ctaText}
            </Button>

            <IconButton
              aria-label="Dismiss promotion"
              onClick={handleClose}
              size="small"
              sx={{
                color: "#4b5563",
                "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
